import { useContext } from "react";
import { NavPage } from "../../services/NavService";
import { CabinetClient, IUserSolution } from "../../services/WebApiClient";
import { useClient } from "../../util/useClient";
import { DisplayDate } from "../DisplayDate";
import { EditorContext } from "../EditorCommon/EditorContext";
import { LoginContext } from "../EditorCommon/LoginContext";
import { IColumn, Table } from "../Table/Table";
import { ErrorFlagBadges } from "./ErrorFlagBadges";
import { OrderButton } from "./OrderButton";



export function UserSolutionTable(props: {
    userSolutions: IUserSolution[];
    orderChanged: () => void;
    onOrderComplete: (us: IUserSolution) => void;
}) {

    const ec = useContext(EditorContext);
    const loginContext = useContext(LoginContext);
    const cabinetClient = useClient(CabinetClient);

    async function openSolution(us: IUserSolution) {
        const cabinetClient = new CabinetClient(undefined, loginContext.loginService.http);
        await ec.cabinetService.loadSavedCabinet(us.id, cabinetClient);
        const navigateResult = await ec.navService.navigate(NavPage.PieceList);
        if (!navigateResult.success) {
            await ec.navService.navigate(NavPage.ProductLine);
        }
    }

    function isDeletable(userSolution: IUserSolution) {
        return userSolution.orderedAt === null;
    }

    async function deleteSolution(userSolution: IUserSolution) {
        if (!isDeletable(userSolution)) {
            //Solution is ordered - cannot be deleted
            return;
        }
        const sure = window.confirm(ec.translator("user solution table delete question", "Er du sikker på du vil slette løsningen '{name}'?", { name: userSolution.name}))
        if (!sure) {
            return;
        }
        await cabinetClient.deleteSolution(userSolution.id);
        props.orderChanged();
    }
    

    const columns: IColumn<IUserSolution>[] = [
        {
            key: "name",
            content: us => (<span>
                {us.name} <ErrorFlagBadges errorFlags={us.errorFlags} />
            </span>),
            header: ec.translator("user solution table col name", "Navn"),
            sort: (a,b) => a.name.localeCompare(b.name)
        },
        {
            key: "createdAt",
            content: us => <DisplayDate date={us.createdAt} />,
            header: ec.translator("user solution table col createdAt", "Oprettet"),
            sort: (a,b) => a.createdAt.valueOf() - b.createdAt.valueOf()
        },
        {
            key: "orderedAt",
            content: us => <DisplayDate date={us.orderedAt} />,
            header: ec.translator("user solution table col orderedAt", "Bestilt"),
            sort: (a, b) => (a.orderedAt?.valueOf() ?? 0) - (b.orderedAt?.valueOf() ?? 0)
        },
        {
            key: "orderNumber",
            content: us => us.orderNumber ?? " -- ",
            header: ec.translator("user solution table col orderNumber", "Tilbudsnummer"),
            sort: (a,b) => a.orderNumber?.localeCompare(b.orderNumber ?? "") ?? -1 //Maybe change this to 1
        },
        {
            key: "btnOpen",
            className: "width-auto",
            content: us => (
                <button className="btn btn-outline-primary"
                    onClick={() => openSolution(us)}
                >
                    {ec.translator("user solution table btn open", "Åbn løsning")}
                </button>
            ),
        },
        {
            key: "btnPdf",
            className: "width-auto",
            content: us => (
                <a className="btn btn-outline-primary"
                    href={us.pdfPath}
                    download={`${us.name}.pdf`}
                >
                    {ec.translator("user solution table btn pdf", "Hent som .pdf")}
                </a>
            ),
        },
        {
            key: "btnOrder",
            className: "width-auto",
            content: us => (
                <OrderButton solution={us}
                    orderChanged={props.orderChanged}
                    onOrderComplete={props.onOrderComplete}
                />
            ),
        },
        {
            key: "btnDelete",
            className: "width-auto",
            content: us => (
                <button className="btn btn-outline-danger"
                    onClick={() => deleteSolution(us)}
                    disabled={!isDeletable(us)}
                >
                    {ec.translator("user solution table btn delete", "Slet løsning")}
                </button>
            )
        },
    ];


    return <Table<IUserSolution>
        columns={columns}
        rows={props.userSolutions}
        rowKey={us => us.id}
        tableProps={{ className: "table table-striped table-hover user-solution-table" }}
    />

}
